/**
 * Seniority helpers for the assessor derogation rule.
 *
 * The org chart (`/api/companies/:code/org-chart`) is an ordered list of tiers, each holding a set
 * of job-role names. The FIRST tier is the most senior, so a smaller tier index means more senior.
 * These helpers resolve a job role to its tier and compare an assessor's tier against the
 * assessee's tier so callers can decide whether a derogation is required.
 */

export interface OrgChartLevel {
  id?: string;
  title?: string;
  roles?: string[];
}

export interface OrgChart {
  levels: OrgChartLevel[];
}

export interface ResolvedTier {
  index: number;
  title: string;
}

/** 'senior' = assessor outranks assessee; 'same' = same tier; 'junior' = assessor is below assessee. */
export type SeniorityRelation = "senior" | "same" | "junior" | "unknown";

function normalizeName(value: string | null | undefined): string {
  return String(value ?? "").trim().toLowerCase();
}

/** Coerce an arbitrary org-chart API payload into a stable {levels:[{title,roles}]} shape. */
export function normalizeOrgChart(raw: unknown): OrgChart {
  const obj = raw && typeof raw === "object" ? (raw as { levels?: unknown }) : {};
  const rawLevels = Array.isArray(obj.levels) ? obj.levels : [];
  const levels: OrgChartLevel[] = rawLevels.map((lvl, idx) => {
    const l = lvl && typeof lvl === "object" ? (lvl as Partial<OrgChartLevel>) : {};
    return {
      id: String(l.id ?? `level-${idx}`),
      title: String(l.title ?? "").trim(),
      roles: Array.isArray(l.roles) ? l.roles.map((r) => String(r ?? "").trim()).filter(Boolean) : [],
    };
  });
  return { levels };
}

/**
 * Resolve a job-role name to its seniority tier. Returns null when the org chart is empty, the role
 * is blank, or the role has not been placed into any tier (rule cannot be evaluated in those cases).
 */
export function resolveTier(orgChart: OrgChart | null | undefined, roleName: string | null | undefined): ResolvedTier | null {
  const target = normalizeName(roleName);
  if (!target || !orgChart || !Array.isArray(orgChart.levels)) return null;
  for (let index = 0; index < orgChart.levels.length; index += 1) {
    const level = orgChart.levels[index];
    const roles = Array.isArray(level?.roles) ? level.roles : [];
    if (roles.some((r) => normalizeName(r) === target)) {
      return { index, title: String(level?.title ?? "").trim() || `Tier ${index + 1}` };
    }
  }
  return null;
}

/**
 * Compare the assessor's tier against the assessee's tier. Returns 'unknown' if either role could
 * not be placed in the org chart, so callers can skip the derogation flow rather than guessing.
 */
export function compareSeniority(
  assessorTier: ResolvedTier | null,
  assesseeTier: ResolvedTier | null,
): SeniorityRelation {
  if (!assessorTier || !assesseeTier) return "unknown";
  if (assessorTier.index === assesseeTier.index) return "same";
  // Smaller index = more senior tier.
  return assessorTier.index < assesseeTier.index ? "senior" : "junior";
}

/**
 * True when the given tier is strictly more senior than the reference tier (used to build the list
 * of suggested higher-seniority assessors for the junior-assessor warning).
 */
export function isMoreSenior(candidateTier: ResolvedTier | null, referenceTier: ResolvedTier | null): boolean {
  if (!candidateTier || !referenceTier) return false;
  return candidateTier.index < referenceTier.index;
}
